import React, { useState } from 'react';
import { StatuteSectionResult, Mismatch, UpstreamBug } from '../types/validation';

interface StatuteSectionProps {
  section: StatuteSectionResult;
}

const MismatchRow: React.FC<{ mismatch: Mismatch }> = ({ mismatch }) => (
  <tr className="mismatch-row">
    <td className="mismatch-desc">{mismatch.description}</td>
    <td className="mismatch-count">{mismatch.count.toLocaleString()}</td>
    <td className="mismatch-explanation">{mismatch.explanation}</td>
    <td className="mismatch-citation">
      <code>{mismatch.citation}</code>
      {mismatch.upstreamIssue && (
        <a href={mismatch.upstreamIssue} target="_blank" rel="noopener noreferrer" className="issue-link">
          Issue ↗
        </a>
      )}
    </td>
  </tr>
);

export const StatuteSection: React.FC<StatuteSectionProps> = ({ section }) => {
  const [collapsed, setCollapsed] = useState(false);
  const matchPercent = (section.summary.matchRate * 100).toFixed(1);

  const formatNumber = (n: number) => n.toLocaleString();

  return (
    <div className="statute-section">
      <div className="section-header" onClick={() => setCollapsed(!collapsed)}>
        <div className="section-title">
          <span className="collapse-icon">{collapsed ? '▶' : '▼'}</span>
          <span className="section-code">{section.section}</span>
          <span className="section-name">{section.title}</span>
          {section.households && (
            <span className="section-households">{formatNumber(section.households)} households</span>
          )}
        </div>
        <div className="section-summary">
          <span
            className={`match-rate ${
              section.summary.matchRate >= 0.99
                ? 'perfect'
                : section.summary.matchRate >= 0.95
                ? 'good'
                : 'warning'
            }`}
          >
            {matchPercent}%
          </span>
          <span className="test-count">
            {formatNumber(section.summary.matches)}/{formatNumber(section.summary.total)}
          </span>
        </div>
      </div>

      {!collapsed && (
        <div className="section-details">
          {/* Validator Breakdown */}
          {section.validatorBreakdown && (
            <div className="validator-breakdown">
              <h4>By Validator</h4>
              <div className="breakdown-grid">
                {Object.entries(section.validatorBreakdown).map(([name, data]) => (
                  <div key={name} className="breakdown-item">
                    <span className="breakdown-name">{name}</span>
                    <span className="breakdown-rate">{(data.rate * 100).toFixed(1)}%</span>
                    <span className="breakdown-counts">
                      {formatNumber(data.matches)}/{formatNumber(data.total)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="section-stats">
            {section.summary.meanAbsoluteError !== undefined && (
              <div className="section-stat">
                <span className="stat-label">MAE</span>
                <span className="stat-value">${section.summary.meanAbsoluteError.toFixed(2)}</span>
              </div>
            )}
            {section.summary.federalMatchRate !== undefined && (
              <div className="section-stat">
                <span className="stat-label">Federal</span>
                <span className="stat-value">{(section.summary.federalMatchRate * 100).toFixed(1)}%</span>
              </div>
            )}
          </div>

          {/* Mismatches Table */}
          {section.mismatches && section.mismatches.length > 0 && (
            <div className="mismatches">
              <h4>Disagreements</h4>
              <table className="mismatches-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Count</th>
                    <th>Explanation</th>
                    <th>Citation</th>
                  </tr>
                </thead>
                <tbody>
                  {section.mismatches.map((m, i) => (
                    <MismatchRow key={i} mismatch={m} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Upstream Bugs */}
          {section.upstreamBugs && section.upstreamBugs.length > 0 && (
            <div className="upstream-bugs">
              <h4>Known Upstream Issues</h4>
              {section.upstreamBugs.map((bug, i) => (
                <div key={i} className="bug-item">
                  <a href={bug.issue} target="_blank" rel="noopener noreferrer">
                    {bug.validator}: {bug.description}
                  </a>
                  <span className="citation">{bug.citation}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
